/**
 * Cloudflare Turnstile on the `try.` landing (AD-794 §7; owner Q-G6: ON as standard from day
 * one). The widget renders in the page before `POST /api/guest/start` and `POST /api/guest/queue`,
 * whose handlers verify the token server-side (S3) — so a bot never costs a seat or a queue place.
 *
 * The SITE KEY is deploy-time client config, `window.__GC_CONFIG__.turnstileSiteKey` (design
 * §2.6 step 4: written by `tools/cloudflare/try-subdomain.mjs` beside the existing `serverUrl`
 * seam in `net/platform.ts`). No key ⇒ no widget and no token: a local guest process boots with
 * `GC_GUEST_TURNSTILE=off` (refused in production), and a production process without a key
 * refuses the start with `guestTurnstileFailed`, which the flow shows as a retry — fail-closed,
 * never a silent bypass.
 *
 * Tokens are SINGLE-USE: `token()` hands out the widget's current token once, then resets the
 * widget so the next call (a queue join after a full start, a "Try again") waits for a fresh one.
 */

/** The deploy-time site key, or null when none is configured. Pure over its input for tests. */
export function turnstileSiteKey(
  cfg: unknown = (globalThis as { __GC_CONFIG__?: unknown }).__GC_CONFIG__,
): string | null {
  if (typeof cfg !== 'object' || cfg === null) return null;
  const key = (cfg as { turnstileSiteKey?: unknown }).turnstileSiteKey;
  return typeof key === 'string' && key.trim() !== '' ? key.trim() : null;
}

/** The slice of the Turnstile global this module drives (explicit rendering). */
export interface TurnstileGlobal {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      action?: string;
      appearance?: 'always' | 'execute' | 'interaction-only';
    },
  ): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let loading: Promise<TurnstileGlobal> | null = null;

/** Inject Cloudflare's script once and resolve with its global. */
export function loadTurnstile(): Promise<TurnstileGlobal> {
  loading ??= new Promise<TurnstileGlobal>((resolve, reject) => {
    const existing = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
    if (existing) {
      resolve(existing);
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      const t = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
      if (t) resolve(t);
      else reject(new Error('turnstile script loaded without its global'));
    };
    script.onerror = () => {
      loading = null;
      reject(new Error('turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** Hands out single-use Turnstile tokens from one widget. */
export interface TurnstileVerifier {
  token(): Promise<string | undefined>;
  dispose(): void;
}

/** The verifier for a page with no site key: no widget, no token (see the module header). */
export const NO_TURNSTILE: TurnstileVerifier = {
  token: () => Promise.resolve(undefined),
  dispose: () => undefined,
};

/**
 * A verifier over one widget in `container`. `global` is injectable for tests; in the page it is
 * `loadTurnstile()`. A widget error resolves the waiting call with `undefined`, so the server
 * refuses it and the flow shows a retry, rather than the page hanging on a challenge that died.
 */
export function createTurnstileVerifier(
  container: HTMLElement,
  siteKey: string,
  global: () => Promise<TurnstileGlobal> = loadTurnstile,
): TurnstileVerifier {
  let widgetId: string | null = null;
  let fresh: string | null = null;
  let waiters: ((token: string | undefined) => void)[] = [];
  let disposed = false;
  let api: TurnstileGlobal | null = null;

  const settle = (token: string | undefined): void => {
    const pending = waiters;
    waiters = [];
    for (const w of pending) w(token);
  };

  const ready = global().then((t) => {
    api = t;
    if (disposed) return t;
    widgetId = t.render(container, {
      sitekey: siteKey,
      action: 'guest-demo',
      appearance: 'interaction-only',
      callback: (token) => {
        if (waiters.length > 0) settle(token);
        else fresh = token;
      },
      'error-callback': () => settle(undefined),
      'expired-callback': () => {
        fresh = null;
      },
    });
    return t;
  });

  return {
    async token() {
      try {
        await ready;
      } catch {
        return undefined; // the script could not load: the server refuses, the flow retries
      }
      if (disposed) return undefined;
      if (fresh !== null) {
        const token = fresh;
        fresh = null;
        // Arm the next one now; single-use means this token is spent by the request it rides.
        if (api !== null && widgetId !== null) api.reset(widgetId);
        return token;
      }
      return new Promise<string | undefined>((resolve) => {
        waiters.push((token) => {
          if (api !== null && widgetId !== null && token !== undefined) api.reset(widgetId);
          resolve(token);
        });
      });
    },
    dispose() {
      disposed = true;
      settle(undefined);
      if (api !== null && widgetId !== null) api.remove(widgetId);
    },
  };
}
